import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

const FALLBACK_SLIDES = [
  {
    id: 'fallback-1',
    image: 'https://images.unsplash.com/photo-1541339907198-e08756dedf3f?w=2000&q=80',
    eyebrow: 'Established 1972',
    headline: 'Enlightening <em>your path</em><br>for over five decades',
    subline: "One of Bangladesh's most prestigious English Medium schools — preparing students for the world's leading universities through the British Curriculum.",
    primaryButtonLabel: 'Apply for Admission',
    primaryButtonUrl: '/admission',
    secondaryButtonLabel: 'Discover Our Story',
    secondaryButtonUrl: '/about',
  },
];

const isInternal = (url) => url && url.startsWith('/');

const HeroLink = ({ to, className, children }) => {
  if (!to) return null;
  return isInternal(to)
    ? <Link to={to} className={className}>{children}</Link>
    : <a href={to} className={className}>{children}</a>;
};

export function HeroSlider({ slides }) {
  const list = (slides && slides.length > 0) ? slides : FALLBACK_SLIDES;
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const rootRef = useRef(null);
  const timerRef = useRef(null);

  useEffect(() => {
    if (paused || list.length < 2) return;
    timerRef.current = setInterval(() => {
      setIndex((i) => (i + 1) % list.length);
    }, 6500);
    return () => clearInterval(timerRef.current);
  }, [paused, list.length]);

  const next = () => setIndex((i) => (i + 1) % list.length);
  const prev = () => setIndex((i) => (i - 1 + list.length) % list.length);

  // Keyboard nav when the hero is focused.
  const onKeyDown = (e) => {
    if (e.key === 'ArrowRight') next();
    if (e.key === 'ArrowLeft') prev();
  };

  return (
    <div
      className="hero"
      ref={rootRef}
      tabIndex={0}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={() => setPaused(false)}
      onKeyDown={onKeyDown}
      aria-roledescription="carousel"
      aria-label="School highlights"
    >
      {list.map((slide, i) => (
        <div
          key={slide.id ?? i}
          className={`hero-slide${i === index ? ' active' : ''}`}
          aria-hidden={i !== index}
        >
          <div className="hero-bg" style={{ backgroundImage: `url('${slide.image}')` }} />
          <div className="hero-content">
            {slide.eyebrow && <div className="hero-eyebrow">{slide.eyebrow}</div>}
            <h1 dangerouslySetInnerHTML={{ __html: slide.headline || '' }} />
            {slide.subline && <p className="hero-sub">{slide.subline}</p>}
            <div className="hero-actions">
              {slide.primaryButtonLabel && slide.primaryButtonUrl && (
                <HeroLink to={slide.primaryButtonUrl} className="btn btn-gold">
                  {slide.primaryButtonLabel}
                </HeroLink>
              )}
              {slide.secondaryButtonLabel && slide.secondaryButtonUrl && (
                <HeroLink to={slide.secondaryButtonUrl} className="btn btn-outline">
                  {slide.secondaryButtonLabel}
                </HeroLink>
              )}
            </div>
          </div>
        </div>
      ))}

      {list.length > 1 && (
        <div className="hero-controls">
          <button className="hero-arrow prev" aria-label="Previous slide" onClick={prev}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="15 18 9 12 15 6" />
            </svg>
          </button>
          <div className="hero-dots">
            {list.map((_, i) => (
              <button
                key={i}
                className={i === index ? 'active' : ''}
                aria-label={`Slide ${i + 1}`}
                aria-current={i === index}
                onClick={() => setIndex(i)}
              />
            ))}
          </div>
          <button className="hero-arrow next" aria-label="Next slide" onClick={next}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="9 18 15 12 9 6" />
            </svg>
          </button>
        </div>
      )}
    </div>
  );
}
